import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const slides = [
  {
    id: 1,
    image: '/images/6fjf12ey_WhatsApp_Image_2026-03-09_at_08.29.41.jpeg',
    headline: 'Ihr Fachbetrieb für Elektrotechnik',
    subheadline: 'in Wuppertal',
    description: 'Qualitativ hochwertige Elektroinstallationen für Privat, Gewerbe und Industrie. Zuverlässig, termingerecht und zu fairen Preisen.',
  },
  {
    id: 2,
    image: '/images/unsplash-1615774925655.jpg',
    headline: 'Zuverlässige Elektrotechnik',
    subheadline: 'Sauber geplant. Perfekt umgesetzt.',
    description: 'Modernste Technologien und hochwertige Materialien für erstklassige Ergebnisse. Von der Beratung bis zur Abnahme.',
  },
  {
    id: 3,
    image: '/images/pexels-photo-9875418.jpeg',
    headline: 'Erneuerbare Energien',
    subheadline: 'Photovoltaik für Ihre Zukunft',
    description: 'Maßgeschneiderte Photovoltaikanlagen für private, gewerbliche und industrielle Anwendungen. Zukunftssicher und effizient.',
  },
  {
    id: 4,
    image: '/images/x822zv4l_WhatsApp_Image_2026-03-05_at_16.03.47__2_.jpeg',
    headline: 'Sicherheit nach höchsten Standards',
    subheadline: 'Zertifiziert nach DIN ISO 9001 & DIN 14675',
    description: 'Brandmeldeanlagen, Sicherheitsbeleuchtung und DGUV-Prüfungen. Normgerecht und zuverlässig.',
  },
];

const usps = [
  { icon: '✓', text: 'Kostenlose Beratung' },
  { icon: '✓', text: 'Vor-Ort-Service' },
  { icon: '✓', text: 'Fachbetrieb-Qualität' },
  { icon: '✓', text: 'Modernste Technik' },
];

// Mobile USP Section - shown only on mobile below hero
export const MobileUSPBar = () => {
  return (
    <div className="sm:hidden bg-white border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 gap-3 py-4">
          {usps.map((usp, index) => (
            <div 
              key={index}
              className="flex items-center gap-2"
            >
              <span className="w-6 h-6 bg-[#E31E24] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 rounded">
                {usp.icon}
              </span>
              <span className="text-xs font-medium text-neutral-800">{usp.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsAnimating(false), 700);
  }, [isAnimating]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsAnimating(false), 700);
  }, [isAnimating]);

  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 700);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative h-[85vh] sm:h-screen min-h-[500px] sm:min-h-[600px] md:min-h-[700px] overflow-hidden">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center hero-bg-animate"
            style={{ backgroundImage: `url(${slide.image})` }}
          />
          
          {/* Overlay - stronger on mobile for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b sm:bg-gradient-to-r from-white via-white/95 sm:via-white/90 to-white/80 sm:to-transparent" />
          
          {/* Content */}
          <div className="relative z-20 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div className="max-w-2xl pt-20 sm:pt-16">
              <div 
                className={`transition-all duration-700 ${
                  index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
                style={{ transitionDelay: '200ms' }}
              >
                <span className="inline-block text-[#E31E24] font-semibold text-xs sm:text-sm uppercase tracking-widest mb-3 sm:mb-4">
                  FESCH Elektrotechnik GmbH
                </span>
              </div>
              
              <h1 
                className={`font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-neutral-900 leading-tight mb-2 transition-all duration-700 ${
                  index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
                style={{ transitionDelay: '300ms' }}
              >
                {slide.headline}
              </h1>
              
              <h2 
                className={`font-semibold text-xl sm:text-2xl md:text-3xl lg:text-4xl text-[#E31E24] mb-4 sm:mb-6 transition-all duration-700 ${
                  index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
                style={{ transitionDelay: '400ms' }}
              >
                {slide.subheadline}
              </h2>
              
              <p 
                className={`text-neutral-600 text-sm sm:text-base lg:text-lg max-w-lg mb-6 sm:mb-8 transition-all duration-700 ${
                  index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
                style={{ transitionDelay: '500ms' }}
              >
                {slide.description}
              </p>
              
              {/* CTAs */}
              <div 
                className={`flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12 transition-all duration-700 ${
                  index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                }`}
                style={{ transitionDelay: '600ms' }}
              >
                <a
                  href="#kontakt"
                  onClick={(e) => scrollToSection(e, '#kontakt')}
                  data-testid="hero-cta-primary"
                  className="btn-primary text-center"
                >
                  Beratung anfragen
                </a>
                <a
                  href="#leistungen"
                  onClick={(e) => scrollToSection(e, '#leistungen')}
                  data-testid="hero-cta-secondary"
                  className="btn-secondary text-center"
                >
                  Leistungen entdecken
                </a>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* USP Bar - Hidden on mobile, shown as absolute on tablet+ */}
      <div className="hidden sm:block absolute bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-sm border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-4 gap-2 lg:gap-4 py-3 lg:py-4">
            {usps.map((usp, index) => (
              <div 
                key={index}
                data-testid={`usp-item-${index}`}
                className="usp-item flex items-center gap-2 lg:gap-3 justify-center"
              >
                <span className="usp-icon w-6 h-6 lg:w-8 lg:h-8 bg-[#E31E24] text-white flex items-center justify-center text-xs lg:text-sm font-bold flex-shrink-0">
                  {usp.icon}
                </span>
                <span className="text-xs lg:text-sm font-medium text-neutral-800 leading-tight">{usp.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Arrows - Hidden on mobile, visible on tablet+ */}
      <button
        onClick={prevSlide}
        data-testid="slider-prev"
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 bg-white/90 hover:bg-white hidden sm:flex items-center justify-center text-neutral-900 hover:text-[#E31E24] transition-all duration-300 shadow-lg"
        aria-label="Vorheriger Slide"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
      </button>
      <button
        onClick={nextSlide}
        data-testid="slider-next"
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 bg-white/90 hover:bg-white hidden sm:flex items-center justify-center text-neutral-900 hover:text-[#E31E24] transition-all duration-300 shadow-lg"
        aria-label="Nächster Slide"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      {/* Dots - adjusted position for mobile */}
      <div className="absolute bottom-16 sm:bottom-24 left-1/2 -translate-x-1/2 z-30 flex gap-2 sm:gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            data-testid={`slider-dot-${index}`}
            className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
            aria-label={`Slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-32 right-8 z-30 hidden lg:flex flex-col items-center gap-2">
        <span className="text-xs font-medium text-neutral-500 uppercase tracking-widest rotate-90 origin-center mb-8">
          Scrollen
        </span>
        <ChevronDown className="w-5 h-5 text-[#E31E24] scroll-indicator" />
      </div>
    </section>
  );
};

export default HeroSlider;
